<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <div class="d-flex justify-content-end align-items-center mb-3">
      <base-button size="sm" type="primary" @click="createObjective">
        <i class="tim-icons el-icon-plus"></i> Add objective
      </base-button>
      <base-button size="sm" type="primary" link @click="openDisplayConfig">
        <i class="tim-icons icon-settings-gear-63 mr-1"></i> Communication screen settings
      </base-button>
    </div>

    <!-- Full-width objective list -->
    <objective-list-panel
      class="mb-2"
      :objectives="objectives"
      :selected-id="selectedId"
      :loading="loading"
      @select="selectObjective"
      @create="createObjective"
      @create-child="createChildObjective"
      @reorder="handleReorder"
    />

    <!-- Drawer -->
    <transition name="slide">
      <div v-if="drawerOpen" class="objective-drawer-overlay" @click.self="closeDrawer">
        <div class="objective-drawer">
          <div class="drawer-header">
            <div>
              <h4 class="mb-0 header-title">
                {{ drawerMode === 'create' ? 'New objective' : (selectedObjective && selectedObjective.name ? selectedObjective.name : 'Untitled objective') }}
              </h4>
            </div>
            <div class="d-flex align-items-center">
              <button v-if="drawerMode !== 'create'" class="header-action-btn mr-2"
                      @click="handleDelete(selectedId)">
                <i class="tim-icons icon-trash-simple"></i>
              </button>
              <button class="close-btn" @click="closeDrawer">
                <i class="tim-icons icon-simple-remove"></i>
              </button>
            </div>
          </div>
          <div class="drawer-body">
            <objective-detail-panel
              ref="detailPanel"
              :key="drawerKey"
              :objective="drawerObjective"
              :game-id="gameId"
              :company-id="companyId"
              :company-group-id="companyGroupId"
              :available-parents="availableParentsForDrawer"
              :is-new="drawerMode === 'create'"
              @saved="onObjectiveSaved"
              @created="onObjectiveCreated"
            />
          </div>
          <div class="drawer-footer">
            <base-button type="primary" size="sm" @click="saveFromFooter">
              {{ drawerMode === 'create' ? 'Create objective' : 'Save objective' }}
            </base-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import swal from 'sweetalert2'
const axios = require('axios')
import ObjectiveListPanel from './ObjectiveListPanel.vue'
import ObjectiveDetailPanel from './ObjectiveDetailPanel.vue'

export default {
  name: 'ObjectiveManager',
  components: { ObjectiveListPanel, ObjectiveDetailPanel },
  props: {
    gameId: { type: [Number, String], required: true },
    companyId: { type: String, required: true },
    companyGroupId: { type: [Number, String], required: true }
  },
  data() {
    return {
      objectives: [],
      selectedId: null,
      loading: false,
      drawerOpen: false,
      drawerMode: 'edit',
      drawerKey: 0,
      drawerParentId: null
    }
  },
  computed: {
    selectedObjective() {
      if (!this.selectedId) return null
      return this.objectives.find(o => o.id === this.selectedId) || null
    },
    drawerObjective() {
      if (this.drawerMode === 'edit' && this.selectedObjective) {
        return this.selectedObjective
      }
      return {
        name: '',
        description: '',
        solution: '',
        solution_img: '',
        parent_id: this.drawerParentId,
        position: this.drawerParentId
          ? this.objectives.filter(o => o.parent_id === this.drawerParentId).length
          : this.objectives.filter(o => !o.parent_id).length
      }
    },
    availableParentsForDrawer() {
      if (this.drawerMode === 'create') {
        return this.objectives.filter(o => !o.parent_id)
      }
      if (!this.selectedId) return []
      return this.objectives.filter(o =>
        !o.parent_id && o.id !== this.selectedId
      )
    }
  },
  methods: {
    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => { resolve(data.idToken.jwtToken) })
          .catch(err => console.log(err))
      })
    },

    async loadObjectives() {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/games/' + this.gameId + '/puzzles'
      try {
        let response = await axios.get(url, config)
        this.objectives = response.data
        this.objectives.sort((a, b) => a.position - b.position)
        this.loading = false
      } catch (err) {
        this.loading = false
        console.log(err)
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden')
        } else if (err.response && err.response.status === 500) {
          swal({
            title: 'Error',
            html: 'The feature has not been enabled as part of your subscription plan.',
            type: 'error',
          }).then(() => { this.$router.push('/') })
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      }
    },

    selectObjective(id) {
      this.selectedId = id
      this.drawerMode = 'edit'
      this.drawerKey++
      this.drawerOpen = true
    },

    createObjective() {
      this.selectedId = null
      this.drawerParentId = null
      this.drawerMode = 'create'
      this.drawerKey++
      this.drawerOpen = true
    },

    createChildObjective(parentId) {
      this.selectedId = null
      this.drawerParentId = parentId
      this.drawerMode = 'create'
      this.drawerKey++
      this.drawerOpen = true
    },

    closeDrawer() {
      this.drawerOpen = false
      this.selectedId = null
    },

    handleEscape(e) {
      if (e.key === 'Escape' && this.drawerOpen) {
        this.closeDrawer()
      }
    },

    async onObjectiveCreated(newObjective) {
      await this.loadObjectives()
      let newId = newObjective.id || newObjective
      this.selectedId = typeof newId === 'object' ? newId.id : parseInt(newId)
      this.drawerMode = 'edit'
      this.drawerKey++
    },

    async onObjectiveSaved(updatedObjective) {
      let idx = this.objectives.findIndex(o => o.id === updatedObjective.id)
      if (idx >= 0) {
        this.$set(this.objectives, idx, { ...this.objectives[idx], ...updatedObjective })
      }
      await this.loadObjectives()
    },

    async handleReorder(reorderedItems) {
      for (let item of reorderedItems) {
        let existing = this.objectives.find(o => o.id === item.id)
        if (existing) existing.position = item.position
      }
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/games/' + this.gameId + '/puzzles'
      let data = reorderedItems.map(item => ({ id: item.id, position: item.position }))
      try {
        await axios.put(url, data, config)
      } catch (err) {
        console.log(err)
      }
    },

    async handleDelete(objectiveId) {
      let objective = this.objectives.find(o => o.id === objectiveId)
      if (!objective) return

      let childCount = this.objectives.filter(o => o.parent_id === objectiveId).length
      let confirmText = "You won't be able to revert this!"
      if (childCount > 0) {
        confirmText = `This will also delete ${childCount} sub-objective${childCount > 1 ? 's' : ''} and all associated hints.`
      }

      let response = await swal({
        title: 'Are you sure?',
        text: confirmText,
        type: 'warning',
        confirmButtonText: 'Yes, delete it!',
        showCancelButton: true
      })

      if (response.value !== true) return

      this.drawerOpen = false
      this.selectedId = null

      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/games/' + this.gameId + '/puzzles/' + objectiveId
      try {
        await axios.delete(url, config)
        await this.loadObjectives()
        swal({ title: 'Objective successfully deleted', type: 'success', confirmButtonText: 'Ok', confirmButtonColor: '#1d8cf8' })
      } catch (err) {
        this.loading = false
        console.log(err)
        if (err.response && err.response.status === 401) {
          swal({
            title: 'Error',
            html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            type: 'error',
          }).then(() => { this.$router.push('/') })
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      }
    },

    openDisplayConfig() {
      let game = { id: this.gameId }
      let stored = localStorage.getItem('game')
      if (stored) {
        try {
          let parsed = JSON.parse(stored)
          if (parsed.id == this.gameId) game = parsed
        } catch (e) { /* use minimal */ }
      }
      localStorage.setItem('game', JSON.stringify(game))
      this.$router.push('/games/clues/configure/display')
    },

    saveFromFooter() {
      if (this.$refs.detailPanel) {
        this.$refs.detailPanel.saveObjective()
      }
    },

  },
  mounted() {
    this.loadObjectives()
    document.addEventListener('keydown', this.handleEscape)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape)
    document.body.style.overflow = ''
  },
  watch: {
    drawerOpen(val) {
      document.body.style.overflow = val ? 'hidden' : ''
    }
  }
}
</script>

<style lang="scss" scoped>
.objective-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
}

.objective-drawer {
  position: fixed;
  top: 0;
  /* Shift left when the wizard side panel is open so both are visible side by side.
     Variable set by OnboardingSidePanel; fallback 0 when no wizard panel. */
  right: var(--wizard-panel-offset, 0);
  transition: right 0.3s ease;
  width: 600px;
  height: 100vh;
  background: #171826;
  display: flex;
  flex-direction: column;
  z-index: 1051;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 100%;
  }
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  background: linear-gradient(180deg, #2d2d44 0%, #252538 100%);
  flex-shrink: 0;
}

.header-title {
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;
  font-size: 16px;
}

.close-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.header-action-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
  }
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
}

.drawer-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  background: linear-gradient(0deg, #2d2d44 0%, #252538 100%);
  flex-shrink: 0;
}

// Slide transition
.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.3s ease;

  .objective-drawer {
    transition: transform 0.3s ease;
  }
}

.slide-enter,
.slide-leave-to {
  opacity: 0;

  .objective-drawer {
    transform: translateX(100%);
  }
}
</style>

<style lang="scss">
// Light mode overrides
.white-content {
  .objective-drawer {
    background: #ffffff;
    box-shadow: -10px 0 40px rgba(0, 0, 0, 0.1);
  }

  .drawer-header {
    border-bottom-color: #e3e3e3;
    background: linear-gradient(180deg, #f8f9fa 0%, #f5f6fa 100%);
  }

  .header-title {
    color: #344675;
  }

  .close-btn,
  .header-action-btn {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #9A9A9A;

    &:hover {
      background: #e9ecef;
      color: #344675;
    }
  }

  .drawer-footer {
    border-top-color: #e3e3e3;
    background: linear-gradient(0deg, #f8f9fa 0%, #f5f6fa 100%);
  }

  .drawer-body {
    &::-webkit-scrollbar-track {
      background: #f5f6fa;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.15);
    }
  }

}
</style>
